import { createSelector } from '@reduxjs/toolkit';

export const selectConsultationWorkflowId = createSelector(
  (state) => state.consultation.currentConsultation,
  (consultation) => {
    return consultation?.workflow;
  },
);

export const selectConsultationWorkflowVersion = createSelector(
  (state) => state.consultation.currentConsultation,
  (consultation) => {
    return consultation?.version;
  },
);

export const selectConsultationTenantId = createSelector(
  (state) => state.consultation.currentConsultation,
  (consultation) => {
    return consultation?.patient?.tenantId;
  },
);

export const selectSummarizedExams = createSelector(
  (state) => state.consultation.currentConsultation,
  (state) => state.consultation.currentConsultation.surveillanceList,
  (_, workflow) => workflow,
  (consultation, surveillanceList, workflow) => {
    const summaryExams = {};
    // This set is a lookup to check is a string is an attachment ID from the consultation.
    // This is used to determine if a field value is an array of attachment IDs or not.
    const attachmentIdSet = new Set(consultation.attachmentIdList ?? []);
    const isAttachmentArray = (value) =>
      Array.isArray(value) && value.length > 0 && value.every((id) => attachmentIdSet.has(String(id)));

    const subExams = workflow?.transitions?.create?.exams_full
      ?.map(({ id, name, pages, parentFormId }) => {
        const isSubExam = parentFormId !== undefined;
        if (isSubExam) {
          return { id, name, pages };
        }
        return null;
      }).filter(Boolean) ?? [];

    // A field can be nested inside Section fields, so we search the field tree recursively.
    const fieldTreeIncludes = (fields, fieldId) =>
      fields?.some((field) => field.id === fieldId || fieldTreeIncludes(field.fields, fieldId)) ?? false;

    // Get the corresponding sub-exam for a field.
    const getCorrespondingSubExam = (fieldId) => {
      const correspondingSubExam = subExams
        ?.flatMap(({ pages, name, id }) => pages.map(page => ({ ...page, subExamId: id, subExamName: name })))
        ?.find(({ fields }) => fieldTreeIncludes(fields, fieldId));
      return correspondingSubExam;
    };

    // The eventList is supposed to be ordered by time of creation, most recent ones at the end.
    // This means we can blindly overwrite the fields while going through the eventList without checking the date.
    consultation.eventList.forEach((event) => {
      // Only the "create" and "close" events are included and displayed in the summary.
      if (event.type === 'create' || event.type === 'close') {
        Object.entries(event.exams)?.forEach(([examName, exam]) => {
          // look up if the exam has a parentForm, in which case this is where we want the exam to go
          const examFromWorkflow = workflow?.transitions[event.type]?.exams_full?.find(({ id }) => id === examName);
          const finalExamName = examFromWorkflow && Object.hasOwn(examFromWorkflow, 'parentFormId') ? examFromWorkflow.parentFormId : examName;
          if (!summaryExams[finalExamName]) {
            // First "create" or "close" event containing this exam, so we create the exam object.
            // Normalize to the same wrapped format { value, isDeleted } used by subsequent events.
            summaryExams[finalExamName] = Object.fromEntries(
              Object.entries(exam).map(([fieldId, fieldValue]) => {
                const correspondingSubExam = getCorrespondingSubExam(fieldId);
                return [
                  fieldId,
                  // Snapshot arrays so we never hold a live reference into the Redux state.
                  {
                    value: Array.isArray(fieldValue) ? [...fieldValue] : fieldValue,
                    isDeleted: fieldValue === '__DELETED__',
                    correspondingSubExam,
                  },
                ];
              }),
            );
          } else {
            // This exam was already set in a previous "create" or "close" event, so we update the fields.
            Object.entries(exam).forEach(([fieldId, fieldValue]) => {
              const correspondingSubExam = getCorrespondingSubExam(fieldId);
              if (isAttachmentArray(fieldValue)) {
                // Photos/files are additive: merge with previous value instead of overwriting.
                const existingIds = summaryExams[finalExamName][fieldId]?.value ?? [];
                const merged = Array.from(new Set([...existingIds, ...fieldValue]));
                summaryExams[finalExamName][fieldId] = { value: merged, isDeleted: false, correspondingSubExam };
              } else {
                // Snapshot arrays so we never hold a live reference into the Redux state.
                const snapshotValue = Array.isArray(fieldValue) ? [...fieldValue] : fieldValue;
                summaryExams[finalExamName][fieldId] = { value: snapshotValue, isDeleted: fieldValue === '__DELETED__', correspondingSubExam };
              }
            });
          }
        });
      }
      // The "dispatch" event has the "smur_idf/dispatch_light" exam.
      // It matches the "smur_id/dispatch" exam from the "create" event (without some tiles and the related cosmetic).
      // We merge the fields from the "dispatch" event into the ones from the "create" event.
      if (event.type === 'dispatch') {
        Object.entries(event.exams)?.forEach(([examName, exam]) => {
          // The exam only has one field, so we can directly overwrite the entire exam.
          // We merge it into the dispatch exam from the "create" event (same exam, without some tiles and cosmetics).
          const dispatchExamKey = workflow?.dispatchExam;
          const correspondingSubExam = getCorrespondingSubExam(dispatchExamKey);
          summaryExams[dispatchExamKey] = { ...summaryExams[examName], ...exam, correspondingSubExam };
        });
      }
    });

    // Adding the key for the surveillence exam is enough here.
    // Later, selectSummarizedWorkflowExams from wokflowSelectors.js will add the full exam object on it.
    // Since surveillance is handled as individual components, all we really care about is the "order" and "id" from the exam.
    if (surveillanceList.length > 0 && workflow?.surveillanceExam) {
      summaryExams[workflow.surveillanceExam] = {};
    }

    return summaryExams;
  },
);

export const selectConsultationAttachmentInfo = createSelector(
  (state) => state.consultation,
  (_, id) => id,
  (consultation, id) => {
    // Need to look at the attachmentList in order to get all photo from the consultation including the one in the patient (outside vitals)
    const allAttachment = consultation.attachmentList;
    return allAttachment?.find((attachment) => attachment.id.toString() === id);
  },
);
